import { useState } from 'react';
import { forms } from '@cwos/api-client';
import { Pill } from '@cwos/ui-admin';
import { SettingsField } from '../settings/common.js';
import { CADENCE_READING } from './formsLogic.js';
import {
  type DraftField,
  type DraftSection,
  type VersionDraft,
  emptyField,
  emptySection,
  revisionLabel,
  revisionSentence,
  type RevisionIntent,
  validateDraft,
} from './formsAuthoring.js';

const FIELD_TYPE_READING: Record<forms.FormFieldType, string> = {
  text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  date: 'Date',
  select: 'Choose one',
  multiselect: 'Choose any',
  yes_no: 'Yes / no',
  attachment: 'File',
  signature: 'Signature',
};

/**
 * WRITING THE QUESTIONS AN ORGANIZATION ASKS.
 *
 * The editor is the same for a first draft and for a revision, and that is the
 * point: a revision is not an edit of the version on screen, it is the NEXT
 * version written from its wording. `intent` carries which of the two this is,
 * and {@link revisionSentence} states the consequence ABOVE the button rather
 * than in a toast afterwards — "this does not change v3; it writes v4 as a
 * draft, and every response already given keeps answering v3".
 *
 * NOTHING TYPED HERE IS EVER MARKUP. Section titles, question labels and
 * choices are tenant content; they go to the server as strings and come back
 * through React text nodes. There is no `dangerouslySetInnerHTML` on this
 * surface and there must never be one.
 *
 * THE VALIDATION IS A COURTESY AND SAYS SO. `schemas.ts` refuses every rule
 * below independently. This exists so an author meets the rule beside the field
 * rather than as a 400 after forty questions.
 */
export interface FormDraftEditorProps {
  draft: VersionDraft;
  onChange: (next: VersionDraft) => void;
  /** Absent for a first draft; present when this will produce version n+1. */
  intent?: RevisionIntent;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /** The server's sentence, when it has already refused once. */
  serverError?: string | null;
}

export function FormDraftEditor({
  draft,
  onChange,
  intent,
  busy,
  onSubmit,
  onCancel,
  serverError,
}: FormDraftEditorProps) {
  const [showProblems, setShowProblems] = useState(false);
  const problems = validateDraft(draft);

  function setSection(id: string, next: Partial<DraftSection>) {
    onChange({
      ...draft,
      sections: draft.sections.map((s) => (s.id === id ? { ...s, ...next } : s)),
    });
  }

  function setField(sectionId: string, fieldId: string, next: Partial<DraftField>) {
    onChange({
      ...draft,
      sections: draft.sections.map((s) =>
        s.id === sectionId
          ? { ...s, fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...next } : f)) }
          : s,
      ),
    });
  }

  return (
    <form
      className="rn-col"
      style={{ gap: 'var(--space-16)', marginTop: 'var(--space-16)' }}
      onSubmit={(e) => {
        e.preventDefault();
        setShowProblems(true);
        if (problems.length === 0) onSubmit();
      }}
    >
      {intent ? (
        <div className="rn-col" style={{ gap: 'var(--space-6)' }}>
          <div className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
            <Pill variant="sky" dot>
              New version v{intent.to}
            </Pill>
          </div>
          {/* THE SENTENCE. Before the press, not after. */}
          <p className="rn-muted">{revisionSentence(intent)}</p>
        </div>
      ) : (
        <p className="rn-muted">
          This writes the first version, as a draft. Nothing is asked of anybody until a person
          approves it and somebody publishes it — two separate steps, in that order.
        </p>
      )}

      {draft.sections.map((section, sectionIndex) => (
        <fieldset
          key={section.id}
          style={{
            border: '1px solid var(--color-line)',
            borderRadius: 'var(--radius-8)',
            padding: 'var(--space-12)',
            display: 'grid',
            gap: 'var(--space-12)',
          }}
        >
          <legend className="rn-muted">Section {sectionIndex + 1}</legend>
          <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
            <SettingsField label="Section title">
              <input
                type="text"
                value={section.title}
                onChange={(e) => setSection(section.id, { title: e.currentTarget.value })}
              />
            </SettingsField>
            <SettingsField
              label="Section key"
              hint="Lower snake_case. It is how answers are filed and it does not change."
            >
              <input
                type="text"
                value={section.key}
                spellCheck={false}
                onChange={(e) => setSection(section.id, { key: e.currentTarget.value })}
              />
            </SettingsField>
          </div>

          {section.fields.map((field) => (
            <div
              key={field.id}
              className="rn-col"
              style={{
                gap: 'var(--space-8)',
                paddingLeft: 'var(--space-12)',
                borderLeft: '2px solid var(--color-line)',
              }}
            >
              <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
                <SettingsField label="Question">
                  <input
                    type="text"
                    value={field.label}
                    onChange={(e) =>
                      setField(section.id, field.id, { label: e.currentTarget.value })
                    }
                  />
                </SettingsField>
                <SettingsField
                  label="Question key"
                  hint="What an answer names. Two questions may never share one."
                >
                  <input
                    type="text"
                    value={field.key}
                    spellCheck={false}
                    onChange={(e) => setField(section.id, field.id, { key: e.currentTarget.value })}
                  />
                </SettingsField>
                <SettingsField label="Answer type">
                  <select
                    value={field.dataType}
                    onChange={(e) =>
                      setField(section.id, field.id, {
                        dataType: e.currentTarget.value as forms.FormFieldType,
                      })
                    }
                  >
                    {(Object.keys(FIELD_TYPE_READING) as forms.FormFieldType[]).map((type) => (
                      <option key={type} value={type}>
                        {FIELD_TYPE_READING[type]}
                      </option>
                    ))}
                  </select>
                </SettingsField>
              </div>

              {forms.fieldTakesOptions(field.dataType) ? (
                <SettingsField label="Choices" hint="One per line.">
                  <textarea
                    rows={3}
                    value={field.optionsText}
                    onChange={(e) =>
                      setField(section.id, field.id, { optionsText: e.currentTarget.value })
                    }
                  />
                </SettingsField>
              ) : null}

              <label className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) =>
                    setField(section.id, field.id, { required: e.currentTarget.checked })
                  }
                />
                <span className="rn-muted">An answer is required</span>
              </label>

              {section.fields.length > 1 ? (
                <div>
                  <button
                    type="button"
                    className="rn-btn-quiet"
                    onClick={() =>
                      setSection(section.id, {
                        fields: section.fields.filter((f) => f.id !== field.id),
                      })
                    }
                  >
                    Remove this question
                  </button>
                </div>
              ) : null}
            </div>
          ))}

          <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => setSection(section.id, { fields: [...section.fields, emptyField()] })}
            >
              Add a question
            </button>
            {draft.sections.length > 1 ? (
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() =>
                  onChange({
                    ...draft,
                    sections: draft.sections.filter((s) => s.id !== section.id),
                  })
                }
              >
                Remove this section
              </button>
            ) : null}
          </div>
        </fieldset>
      ))}

      <div>
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => onChange({ ...draft, sections: [...draft.sections, emptySection()] })}
        >
          Add a section
        </button>
      </div>

      <SettingsField label="How often it should be answered">
        <select
          value={draft.defaultCadence}
          onChange={(e) =>
            onChange({ ...draft, defaultCadence: e.currentTarget.value as forms.FormCadence })
          }
        >
          {(Object.keys(CADENCE_READING) as forms.FormCadence[]).map((cadence) => (
            <option key={cadence} value={cadence}>
              {CADENCE_READING[cadence]}
            </option>
          ))}
        </select>
      </SettingsField>

      <label className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={draft.requiresAttestation}
          onChange={(e) => onChange({ ...draft, requiresAttestation: e.currentTarget.checked })}
        />
        <span className="rn-muted">
          The person answering must attest to what they have recorded
        </span>
      </label>
      {draft.requiresAttestation ? (
        <SettingsField
          label="What they are attesting to"
          hint="Shown to the person answering, and kept with their response."
        >
          <textarea
            rows={2}
            value={draft.attestationPrompt}
            onChange={(e) => onChange({ ...draft, attestationPrompt: e.currentTarget.value })}
          />
        </SettingsField>
      ) : null}

      {showProblems && problems.length > 0 ? (
        <div role="alert" className="rn-col" style={{ gap: 'var(--space-4)' }}>
          <strong>Fix these before saving</strong>
          <ul style={{ paddingLeft: 'var(--space-16)' }}>
            {problems.map((problem, i) => (
              <li key={`${problem.where}-${i}`}>
                <strong>{problem.where}</strong> — {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {serverError ? (
        <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
          {serverError}
        </p>
      ) : null}

      <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
        {/* NOT disabled on `problems.length > 0`. A disabled button with no
            explanation is the shape that leaves an author guessing; pressing it
            shows every problem at once and sends nothing. */}
        <button type="submit" className="rn-btn" disabled={busy}>
          {intent ? revisionLabel(intent) : 'Save this draft'}
        </button>
        <button type="button" className="rn-btn-quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
